/**
 * FERP Audit Trail Service - Life-Critical Compliance System
 *
 * Provides immutable audit trails for all user actions in life-critical workflows.
 * MANDATORY for compliance in healthcare, financial, and legal domains.
 *
 * CRITICAL: All user actions that modify data MUST generate audit entries.
 * This is not optional - patient safety and legal compliance depend on it.
 */

import { useGraphQLClient } from '../hooks/useGraphQLClient';
import { useAuth } from '../security/useAuth';

// Core audit trail entry structure
export interface AuditEntry {
  id: string;
  tenantId: string;
  userId: string;
  action: string;
  resource: string;
  resourceId: string;
  details: Record<string, unknown>;
  timestamp: string;
  ipAddress?: string;
  userAgent?: string;
  sessionId?: string;
  // Immutability verification
  previousHash?: string;
  currentHash: string;
}

// Audit query interfaces
export interface AuditQuery {
  tenantId: string;
  userId?: string;
  action?: string;
  resource?: string;
  dateFrom?: Date;
  dateTo?: Date;
  limit?: number;
  offset?: number;
}

export interface AuditSearchResult {
  entries: AuditEntry[];
  totalCount: number;
  hasMore: boolean;
}

// Action types for standardized audit trails
export const AUDIT_ACTIONS = {
  // Data modification actions
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',

  // Workflow actions
  WORKFLOW_START: 'WORKFLOW_START',
  WORKFLOW_COMPLETE: 'WORKFLOW_COMPLETE',
  WORKFLOW_CANCEL: 'WORKFLOW_CANCEL',
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
  ESCALATE: 'ESCALATE',

  // Security actions
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  PERMISSION_CHANGE: 'PERMISSION_CHANGE',
  EMERGENCY_OVERRIDE: 'EMERGENCY_OVERRIDE',

  // Safety actions
  SAFETY_THRESHOLD_CHANGE: 'SAFETY_THRESHOLD_CHANGE',
  COMPLIANCE_VIOLATION: 'COMPLIANCE_VIOLATION',
  EMERGENCY_PROCEDURE: 'EMERGENCY_PROCEDURE',

  // Configuration actions
  CONFIG_CHANGE: 'CONFIG_CHANGE',
  PARAMETER_UPDATE: 'PARAMETER_UPDATE',
  SCHEMA_CHANGE: 'SCHEMA_CHANGE'
} as const;

export type AuditAction = typeof AUDIT_ACTIONS[keyof typeof AUDIT_ACTIONS];

/**
 * AuditTrailService - Immutable audit logging for life-critical systems
 *
 * Ensures all user actions are logged with cryptographic integrity for
 * compliance with healthcare, financial, and legal regulatory requirements.
 */
export class AuditTrailService {
  private graphqlClient: ReturnType<typeof useGraphQLClient>;
  private authService: ReturnType<typeof useAuth>;

  constructor(
    graphqlClient: ReturnType<typeof useGraphQLClient>,
    authService: ReturnType<typeof useAuth>
  ) {
    this.graphqlClient = graphqlClient;
    this.authService = authService;
  }

  /**
   * Create audit entry for user action
   * MANDATORY for all data-modifying operations
   */
  async logAction(
    action: AuditAction,
    resource: string,
    resourceId: string,
    details: Record<string, unknown> = {}
  ): Promise<AuditEntry> {
    try {
      const tenantId = this.authService.getCurrentTenantId();
      const userId = this.authService.getCurrentUserId();

      // Generate audit entry with immutability hash
      const auditEntry: Omit<AuditEntry, 'id' | 'currentHash' | 'previousHash'> = {
        tenantId,
        userId,
        action,
        resource,
        resourceId,
        details: {
          ...details,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        },
        timestamp: new Date().toISOString(),
        sessionId: this.authService.getSessionId?.() || 'unknown'
      };

      const mutation = `
        mutation CreateAuditEntry($input: AuditEntryInput!) {
          createAuditEntry(input: $input) {
            id
            tenantId
            userId
            action
            resource
            resourceId
            details
            timestamp
            currentHash
            previousHash
          }
        }
      `;

      const response = await this.graphqlClient.mutate({
        mutation,
        variables: { input: auditEntry }
      });

      const createdEntry = response.data?.createAuditEntry;

      console.log(`[AUDIT TRAIL] Action logged: ${action} on ${resource}:${resourceId} by user ${userId}`);

      return createdEntry;

    } catch (error) {
      console.error(`[AUDIT FAILURE] Failed to log action ${action}:`, error);

      // CRITICAL: Audit failures must not break the system but must be logged
      // In production, this would trigger alerts to security teams
      this.logAuditFailure(action, resource, resourceId, error);

      throw new Error(`Audit trail failure: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Search audit trail entries
   * Used for compliance reporting and investigation
   */
  async searchAuditTrail(query: AuditQuery): Promise<AuditSearchResult> {
    try {
      const searchQuery = `
        query SearchAuditTrail($query: AuditQueryInput!) {
          searchAuditTrail(query: $query) {
            entries {
              id
              tenantId
              userId
              action
              resource
              resourceId
              details
              timestamp
              currentHash
              previousHash
            }
            totalCount
            hasMore
          }
        }
      `;

      const response = await this.graphqlClient.query({
        query: searchQuery,
        variables: { query }
      });

      console.log(`[AUDIT SEARCH] Retrieved ${response.data?.searchAuditTrail.entries.length} entries for tenant ${query.tenantId}`);

      return response.data?.searchAuditTrail;

    } catch (error) {
      console.error('[AUDIT SEARCH FAILURE] Failed to search audit trail:', error);
      throw new Error(`Audit search failure: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Verify audit trail integrity
   * Checks cryptographic hash chain for tampering
   */
  async verifyAuditIntegrity(tenantId: string, dateFrom: Date, dateTo: Date): Promise<{
    valid: boolean;
    totalEntries: number;
    invalidEntries: string[];
    lastValidEntry?: string;
  }> {
    try {
      const verifyQuery = `
        query VerifyAuditIntegrity($tenantId: String!, $dateFrom: String!, $dateTo: String!) {
          verifyAuditIntegrity(tenantId: $tenantId, dateFrom: $dateFrom, dateTo: $dateTo) {
            valid
            totalEntries
            invalidEntries
            lastValidEntry
          }
        }
      `;

      const response = await this.graphqlClient.query({
        query: verifyQuery,
        variables: {
          tenantId,
          dateFrom: dateFrom.toISOString(),
          dateTo: dateTo.toISOString()
        }
      });

      const result = response.data.verifyAuditIntegrity;

      if (!result.valid) {
        console.error(`[AUDIT INTEGRITY FAILURE] Audit trail compromised for tenant ${tenantId}`, result);
      } else {
        console.log(`[AUDIT INTEGRITY] Verified ${result.totalEntries} entries for tenant ${tenantId}`);
      }

      return result;

    } catch (error) {
      console.error('[AUDIT INTEGRITY FAILURE] Failed to verify audit integrity:', error);
      throw new Error(`Audit integrity verification failure: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Log emergency override actions
   * Special audit category for life-critical emergency procedures
   */
  async logEmergencyOverride(
    reason: string,
    originalValue: unknown,
    newValue: unknown,
    approver: string,
    durationHours: number = 24
  ): Promise<AuditEntry> {
    return this.logAction(
      AUDIT_ACTIONS.EMERGENCY_OVERRIDE,
      'SAFETY_OVERRIDE',
      `emergency-${Date.now()}`,
      {
        reason,
        originalValue,
        newValue,
        approver,
        durationHours,
        emergencyLevel: 'CRITICAL',
        requiresReview: true
      }
    );
  }

  /**
   * Log compliance violations
   * Required for regulatory compliance reporting
   */
  async logComplianceViolation(
    framework: string,
    violation: string,
    severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL',
    mitigation?: string
  ): Promise<AuditEntry> {
    return this.logAction(
      AUDIT_ACTIONS.COMPLIANCE_VIOLATION,
      'COMPLIANCE',
      `violation-${Date.now()}`,
      {
        framework,
        violation,
        severity,
        mitigation,
        requiresReporting: severity === 'CRITICAL'
      }
    );
  }

  /**
   * Log safety threshold changes
   * Critical for life-critical systems
   */
  async logSafetyThresholdChange(
    thresholdType: string,
    oldValue: number,
    newValue: number,
    justification: string
  ): Promise<AuditEntry> {
    return this.logAction(
      AUDIT_ACTIONS.SAFETY_THRESHOLD_CHANGE,
      'SAFETY_THRESHOLD',
      thresholdType,
      {
        thresholdType,
        oldValue,
        newValue,
        justification,
        riskLevel: this.calculateRiskLevel(oldValue, newValue),
        requiresApproval: true
      }
    );
  }

  /**
   * Log safety alert events
   * Required for life-critical test scenarios
   */
  async logSafetyAlert(
    alertType: string,
    severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL',
    message: string,
    context?: Record<string, unknown>
  ): Promise<AuditEntry> {
    return this.logAction(
      'SAFETY_ALERT' as AuditAction,
      'SAFETY_MONITORING',
      `alert-${Date.now()}`,
      {
        alertType,
        severity,
        message,
        context,
        requiresAcknowledgment: severity === 'CRITICAL',
        timestamp: new Date().toISOString()
      }
    );
  }

  /**
   * Log alert acknowledgment events
   * Required for life-critical test scenarios
   */
  async logAlertAcknowledgment(
    alertId: string,
    acknowledgedBy: string,
    acknowledgmentReason?: string
  ): Promise<AuditEntry> {
    return this.logAction(
      'ALERT_ACKNOWLEDGMENT' as AuditAction,
      'SAFETY_MONITORING',
      alertId,
      {
        alertId,
        acknowledgedBy,
        acknowledgmentReason,
        acknowledgmentTime: new Date().toISOString(),
        complianceRequired: true
      }
    );
  }

  // Private helper methods
  private async logAuditFailure(action: string, resource: string, resourceId: string, error: any): Promise<void> {
    try {
      // Log audit failure to local storage or emergency logging system
      const failureLog = {
        timestamp: new Date().toISOString(),
        action,
        resource,
        resourceId,
        error: error.message,
        tenant: this.authService.getCurrentTenantId(),
        user: this.authService.getCurrentUserId()
      };

      // In development, log to console. In production, this would go to emergency logging
      console.error('[AUDIT FAILURE LOG]', failureLog);

      // Store in localStorage as backup
      const failures = JSON.parse(localStorage.getItem('audit_failures') || '[]');
      failures.push(failureLog);
      localStorage.setItem('audit_failures', JSON.stringify(failures.slice(-100))); // Keep last 100

    } catch (e) {
      // Even audit failure logging failed - this is a critical system error
      console.error('[CRITICAL] Cannot log audit failure:', e);
    }
  }

  private calculateRiskLevel(oldValue: number, newValue: number): 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' {
    const change = Math.abs(newValue - oldValue) / oldValue;

    if (change > 0.5) return 'CRITICAL';
    if (change > 0.25) return 'HIGH';
    if (change > 0.1) return 'MEDIUM';
    return 'LOW';
  }

  /**
   * Log entity search operations
   * Required for GenericEntitySearch component
   */
  async logEntitySearch(
    searchType: string,
    query: string,
    resultCount: number,
    context?: Record<string, unknown>
  ): Promise<AuditEntry> {
    return this.logAction(
      'ENTITY_SEARCH' as AuditAction,
      'SEARCH_OPERATION',
      `search-${Date.now()}`,
      {
        searchType,
        query,
        resultCount,
        context,
        timestamp: new Date().toISOString()
      }
    );
  }

  /**
   * Log entity access operations
   * Required for GenericEntitySearch component
   */
  async logEntityAccess(
    entityType: string,
    entityId: string,
    accessType: 'VIEW' | 'SELECT' | 'DOWNLOAD',
    context?: Record<string, unknown>
  ): Promise<AuditEntry> {
    return this.logAction(
      'ENTITY_ACCESS' as AuditAction,
      entityType,
      entityId,
      {
        accessType,
        context,
        timestamp: new Date().toISOString()
      }
    );
  }

  /**
   * Log data export operations
   * Required for GenericEntitySearch component
   */
  async logDataExport(
    dataType: string,
    format: string,
    recordCount: number,
    context?: Record<string, unknown>
  ): Promise<AuditEntry> {
    return this.logAction(
      'DATA_EXPORT' as AuditAction,
      'DATA_EXPORT',
      `export-${Date.now()}`,
      {
        dataType,
        format,
        recordCount,
        context,
        timestamp: new Date().toISOString(),
        complianceRequired: true
      }
    );
  }
}

/**
 * Hook for using AuditTrailService in React components
 */
export function useAuditTrail(): AuditTrailService {
  const graphqlClient = useGraphQLClient();
  const authService = useAuth();

  return new AuditTrailService(graphqlClient, authService);
}

/**
 * Helper hook for quick action logging
 */
export function useAuditLogger() {
  const auditService = useAuditTrail();

  return {
    logCreate: (resource: string, resourceId: string, details?: Record<string, unknown>) =>
      auditService.logAction(AUDIT_ACTIONS.CREATE, resource, resourceId, details),

    logUpdate: (resource: string, resourceId: string, details?: Record<string, unknown>) =>
      auditService.logAction(AUDIT_ACTIONS.UPDATE, resource, resourceId, details),

    logDelete: (resource: string, resourceId: string, details?: Record<string, unknown>) =>
      auditService.logAction(AUDIT_ACTIONS.DELETE, resource, resourceId, details),

    logWorkflowAction: (action: AuditAction, workflowId: string, details?: Record<string, unknown>) =>
      auditService.logAction(action, 'WORKFLOW', workflowId, details),

    logUserAction: (params: { action: string; details?: Record<string, unknown>; metadata?: Record<string, unknown> }) =>
      auditService.logAction(
        params.action as AuditAction,
        'USER_ACTION',
        `action-${Date.now()}`,
        { ...params.details, metadata: params.metadata }
      ),

    logEmergencyOverride: auditService.logEmergencyOverride.bind(auditService),
    logComplianceViolation: auditService.logComplianceViolation.bind(auditService),
    logSafetyThresholdChange: auditService.logSafetyThresholdChange.bind(auditService)
  };
}


export default AuditTrailService;